/**
 * Type definitions for the admin panel
 * These should match the backend types
 */

export interface OrderItem {
  sku: string;
  name: string;
  quantity: number;
}

export interface Customer {
  first_name: string;
  last_name: string;
  company?: string;
  phone: string;
  email?: string;
}

export interface Shipping {
  address: string;
  postcode: string;
  city: string;
  delivery_method: 'paczkomat' | 'kurier';
  paczkomat_code?: string;
}

export type OrderStatus = 'pending' | 'processing' | 'ordered_awaiting_payment' | 'completed' | 'failed' | 'partial' | 'reservation' | 'ordered_by_woocommerce' | 'product_unavailable';

export type OrderType = 'normal' | 'reservation';
export type PaymentStatus = 'paid' | 'reservation';

/**
 * WooCommerce sync status - tracks whether order is still valid in WooCommerce
 * Used to avoid repeated API calls for orders that are no longer in wc-processing status
 */
export type WcSyncStatus = 'synced' | 'not_found_in_wc' | 'wc_status_changed' | 'wc_trashed' | 'needs_cleanup' | 'closed';

// WooCommerce metadata for order
export interface WcOrderMeta {
  order_comments?: string;
  order_complaint?: string;
  customer_note?: string;
  payment_method?: string;
  payment_method_title?: string;
  shipping_method?: string;
  shipping_total?: string;
  total?: string;
  currency?: string;
}

export interface Order {
  id: string;
  woocommerce_order_id: string;
  supplier_order_id?: string;
  status: OrderStatus;
  wc_status?: string; // WooCommerce order status (e.g., 'processing', 'completed', etc.)
  wc_sync_status?: WcSyncStatus; // Tracks if order is still valid in WC for syncing
  wc_meta?: WcOrderMeta; // Additional WooCommerce metadata
  order_type?: OrderType;
  payment_status?: PaymentStatus; // 'paid' or 'reservation'
  created_at: Date | { seconds: number; nanoseconds: number };
  updated_at: Date | { seconds: number; nanoseconds: number };
  processing_started_at?: Date | { seconds: number; nanoseconds: number };
  processing_completed_at?: Date | { seconds: number; nanoseconds: number };
  customer: Customer;
  shipping: Shipping;
  items: OrderItem[];
  successful_items: string[];
  failed_items: string[];
  total_amount?: number;
  supplier_total_cost?: number; // Cost from supplier confirmation page
  error_message?: string;
  retry_count: number;
  execution_logs?: string[];
  // Supplier payment tracking
  supplier_payment_exported?: Date | { seconds: number; nanoseconds: number };
  supplier_payment_batch_id?: string;
}

export interface LogEntry {
  id: string;
  order_id: string;
  woocommerce_order_id: string;
  timestamp: Date | { seconds: number; nanoseconds: number };
  level: 'info' | 'warn' | 'error';
  message: string;
  details?: Record<string, any>;
}

export interface QueueStatus {
  id: string;
  total_pending: number;
  currently_processing: boolean;
  current_order_id?: string;
  last_updated: Date | { seconds: number; nanoseconds: number };
}

/**
 * Bot status for live preview
 */
export interface BotStatus {
  id: string;
  state: 'idle' | 'processing';
  current_order_id?: string;
  woocommerce_order_id?: string;
  current_step?: string;
  step_number?: number;
  total_steps?: number;
  step_status?: 'running' | 'success' | 'error';
  step_details?: string;
  started_at?: Date | { seconds: number; nanoseconds: number };
  updated_at: Date | { seconds: number; nanoseconds: number };
  items_processed?: number;
  items_total?: number;
  // Extended fields for more details
  current_product?: string;
  last_action?: string;
  current_url?: string;
  error_message?: string;
  // Live console logs (last N entries)
  console_logs?: ConsoleLogEntry[];
}

/**
 * Console log entry for live preview
 */
export interface ConsoleLogEntry {
  timestamp: Date | { seconds: number; nanoseconds: number };
  level: 'info' | 'warn' | 'error' | 'debug' | 'step';
  message: string;
  details?: string;
}

/**
 * Converts Firestore Timestamp to Date
 */
export function toDate(timestamp: Date | { seconds: number; nanoseconds: number }): Date {
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp.seconds * 1000);
}
